// Canonical + hreflang for the Spanish (/es) and Portuguese (/pt) versions of
// a page (Next i18n routing, see next.config.mjs).
//
// Translated pages pass their English canonical through localeSeo(): each
// language version is its own canonical and lists the others. Pages that are
// not translated still exist under /es and /pt (Next routes every page), so
// NonLocalizedGuard marks those noindex with the English page as canonical.
import Head from "next/head";
import { useRouter } from "next/router";
import {
  LOCALES,
  LOCALE_META,
  asLocale,
  type Locale,
} from "@jammable/ui-core/src/i18n";

export const SITE_URL = "https://www.jammable.com";

// English URL or path -> the same page in `locale`, absolute.
export function localeUrl(locale: Locale, urlOrPath: string): string {
  // Any absolute URL counts as its path: callers sometimes build URLs from
  // BASE_URL, which is the preview or localhost origin outside production.
  const path = /^https?:\/\//.test(urlOrPath)
    ? urlOrPath.replace(/^https?:\/\/[^/]*/, "") || "/"
    : urlOrPath;
  const p = path === "/" ? "" : path;
  return `${SITE_URL}${locale === "en" ? "" : `/${locale}`}${p}`;
}

// A path in `locale` (unprefixed for English). For getServerSideProps
// redirects: Next adds the locale prefix to next/link and router.push, but a
// gSSP redirect destination is used as written, so /es/history went to the
// English /library.
export function localePath(locale: string | undefined, path: string): string {
  const l = asLocale(locale);
  return l === "en" ? path : `/${l}${path === "/" ? "" : path}`;
}

// Props for <NextSeo>. `locales`: the languages this page is indexable in
// (default all three); every version must be given the same list.
export function localeSeo(
  locale: Locale,
  englishCanonical: string,
  {
    locales = LOCALES,
    noindex = false,
  }: { locales?: readonly Locale[]; noindex?: boolean } = {},
) {
  const alternates =
    !noindex && locales.length > 1 && locales.includes(locale)
      ? [
          ...locales.map((l) => ({
            hrefLang: LOCALE_META[l].hreflang,
            href: localeUrl(l, englishCanonical),
          })),
          {
            hrefLang: "x-default",
            href: localeUrl(
              locales.includes("en") ? "en" : locales[0],
              englishCanonical,
            ),
          },
        ]
      : undefined;
  return {
    canonical: localeUrl(locale, englishCanonical),
    languageAlternates: alternates,
    noindex: noindex || !locales.includes(locale),
    ogLocale: LOCALE_META[locale].og,
  };
}

// Routes whose content is translated. Keep in step with the pages that call
// localeSeo().
export const LOCALIZED_ROUTES = new Set([
  "/",
  "/[model]",
  "/models",
  "/ai-voices/[category]",
  "/train",
  "/pricing",
  "/create",
  "/hub/[slug]",
]);

export function NonLocalizedGuard() {
  const { locale, pathname, asPath } = useRouter();
  if (asLocale(locale) === "en" || LOCALIZED_ROUTES.has(pathname)) return null;
  // Same keys as next-seo, so these replace the page's own tags.
  return (
    <Head>
      <meta key="robots" name="robots" content="noindex,follow" />
      <link
        key="canonical"
        rel="canonical"
        href={`${SITE_URL}${asPath.split(/[?#]/)[0]}`}
      />
    </Head>
  );
}
